import { useState, useEffect } from "react";

/**
 * Modal for assigning pending quote tickets to real jobs.
 * Shows tickets that were tracked against quotes before orders existed,
 * and now can be assigned to actual jobs.
 */
export default function AssignTicketModal({ tickets, onClose, onAssign }) {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [orders, setOrders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");

  // Filter to only show tickets that are ready for assignment
  const readyTickets = tickets.filter((t) => t.readyForAssignment);

  // Load orders when a ticket is selected
  useEffect(() => {
    if (!selectedTicket) {
      setOrders([]);
      setSelectedOrder(null);
      return;
    }

    async function loadOrders() {
      setLoadingOrders(true);
      setError("");
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        const res = await fetch(
          `/api/orders?quoteNumber=${encodeURIComponent(selectedTicket.quoteNumber)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setOrders(data.orders || []);
      } catch (err) {
        console.error("Error loading orders:", err);
        setError("Failed to load orders");
      } finally {
        setLoadingOrders(false);
      }
    }

    loadOrders();
  }, [selectedTicket]);

  // Load jobs when an order is selected
  useEffect(() => {
    if (!selectedOrder) {
      setJobs([]);
      setSelectedJob(null);
      return;
    }

    async function loadJobs() {
      setLoadingJobs(true);
      setError("");
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        const res = await fetch(
          `/api/orders/${encodeURIComponent(selectedOrder.orderNumber)}/jobs`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setJobs(data.jobs || []);
      } catch (err) {
        console.error("Error loading jobs:", err);
        setError("Failed to load jobs");
      } finally {
        setLoadingJobs(false);
      }
    }

    loadJobs();
  }, [selectedOrder]);

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedTicket || !selectedJob) return;

    setAssigning(true);
    setError("");

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/time/tickets/${selectedTicket.id}/assign`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: selectedJob.id,
          jobNumber: selectedJob.jobNumber,
          jobName: selectedJob.name,
          orderNumber: selectedOrder.orderNumber,
          qty: selectedJob.qty,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to assign ticket");
      }

      // Success - notify parent and reset selection
      onAssign?.(selectedTicket.id);
      setSelectedTicket(null);
      setSelectedOrder(null);
      setSelectedJob(null);

      // If no more tickets, close the modal
      const remaining = readyTickets.filter((t) => t.id !== selectedTicket.id);
      if (remaining.length === 0) {
        onClose();
      }
    } catch (err) {
      console.error("Error assigning ticket:", err);
      setError(err.message || "Failed to assign ticket");
    } finally {
      setAssigning(false);
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  };

  // Format date
  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#1a1a1a",
          borderRadius: 16,
          width: "100%",
          maxWidth: 700,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #f59e0b",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #333",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "rgba(245, 158, 11, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: 24, height: 24, color: "#f59e0b" }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, color: "#fff", fontWeight: 600 }}>
                Assign Pending Tickets
              </h2>
              <div style={{ fontSize: 13, color: "#a1a1aa", marginTop: 2 }}>
                {readyTickets.length} ticket{readyTickets.length !== 1 ? "s" : ""} ready for assignment
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#71717a",
              cursor: "pointer",
              padding: 8,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: 24, height: 24 }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {error && (
            <div
              style={{
                padding: 12,
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: 8,
                marginBottom: 16,
                color: "#fca5a5",
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {/* Step 1: Select Ticket */}
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 12,
                color: "#a1a1aa",
                textTransform: "uppercase",
                marginBottom: 8,
                fontWeight: 600,
              }}
            >
              Step 1: Select a Ticket
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {readyTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => {
                    setSelectedTicket(ticket);
                    setSelectedOrder(null);
                    setSelectedJob(null);
                  }}
                  style={{
                    padding: 12,
                    backgroundColor:
                      selectedTicket?.id === ticket.id ? "rgba(245, 158, 11, 0.2)" : "#111",
                    border:
                      selectedTicket?.id === ticket.id
                        ? "1px solid #f59e0b"
                        : "1px solid #333",
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
                        Quote #{ticket.quoteNumber}
                      </div>
                      <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 2 }}>
                        {ticket.projectName || ticket.jobName || "—"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13, color: "#60a5fa", fontWeight: 500 }}>
                        {ticket.operationCode}
                      </div>
                      <div style={{ fontSize: 12, color: "#a1a1aa" }}>
                        {formatDuration(ticket.durationSeconds)} • {formatDate(ticket.startTime)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step 2: Select Order */}
          {selectedTicket && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#a1a1aa",
                  textTransform: "uppercase",
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                Step 2: Select an Order
              </div>
              {loadingOrders ? (
                <div style={{ padding: 16, textAlign: "center", color: "#71717a" }}>
                  Loading orders...
                </div>
              ) : orders.length === 0 ? (
                <div style={{ padding: 16, textAlign: "center", color: "#71717a" }}>
                  No orders found for this quote
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {orders.map((order) => (
                    <div
                      key={order.orderNumber}
                      onClick={() => {
                        setSelectedOrder(order);
                        setSelectedJob(null);
                      }}
                      style={{
                        padding: 12,
                        backgroundColor:
                          selectedOrder?.orderNumber === order.orderNumber
                            ? "rgba(59, 130, 246, 0.2)"
                            : "#111",
                        border:
                          selectedOrder?.orderNumber === order.orderNumber
                            ? "1px solid #3b82f6"
                            : "1px solid #333",
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
                            Order #{order.orderNumber}
                          </div>
                          <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 2 }}>
                            {order.customerName || order.customer || "—"}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: "#71717a" }}>
                          {order.status || "Active"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Job */}
          {selectedOrder && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#a1a1aa",
                  textTransform: "uppercase",
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                Step 3: Select a Job
              </div>
              {loadingJobs ? (
                <div style={{ padding: 16, textAlign: "center", color: "#71717a" }}>
                  Loading jobs...
                </div>
              ) : jobs.length === 0 ? (
                <div style={{ padding: 16, textAlign: "center", color: "#71717a" }}>
                  No jobs found for this order
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      onClick={() => setSelectedJob(job)}
                      style={{
                        padding: 12,
                        backgroundColor:
                          selectedJob?.id === job.id ? "rgba(16, 185, 129, 0.2)" : "#111",
                        border:
                          selectedJob?.id === job.id
                            ? "1px solid #10b981"
                            : "1px solid #333",
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
                            {job.jobNumber}
                          </div>
                          <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 2 }}>
                            {job.name || job.productCode || "—"}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 13, color: "#fbbf24" }}>
                            Qty: {job.qty || "—"}
                          </div>
                          <div style={{ fontSize: 11, color: "#71717a" }}>
                            {job.productCode}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #333",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "12px 20px",
              fontSize: 14,
              backgroundColor: "transparent",
              color: "#a1a1aa",
              border: "1px solid #3f3f46",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Close
          </button>

          <button
            onClick={handleAssign}
            disabled={!selectedJob || assigning}
            style={{
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              backgroundColor: !selectedJob || assigning ? "#374151" : "#10b981",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: !selectedJob || assigning ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {assigning ? (
              <>
                <svg
                  style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }}
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Assigning...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 16, height: 16 }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Assign to Job
              </>
            )}
          </button>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
